import type { FC } from 'react';
import { useState } from 'react';
import { Box, Center, Image } from '@chakra-ui/react';

import clipboardImg from 'assets/brand/clipboard@2x.png';
import SunshineCard from 'components/_core/SunshineCard';
import Upload, { FILE_TYPE, UploadMessage } from 'components/Upload';
import { UploadedFileInfo } from 'components/Upload/types';
import { uploadReceipt } from 'features/Storage/upload';

import locales from './locales';
import ReceiptUploadItem from './ReceiptUploadItem';

interface Props {
  receipt?: ReceiptType | null;
  onReceiptUploaded: (receipt: ReceiptType) => void;
  onReceiptRemoved: (receipt: ReceiptType) => void;
}

enum ReceiptUploadState {
  UploadReceipt = 'UPLOAD_RECEIPT',
  ReceiptDetail = 'RECEIPT_DETAIL',
}

const ReceiptUpload: FC<Props> = ({
  onReceiptRemoved,
  onReceiptUploaded,
  receipt,
}) => {
  const [state, setState] = useState<ReceiptUploadState>(
    receipt
      ? ReceiptUploadState.ReceiptDetail
      : ReceiptUploadState.UploadReceipt,
  );
  const showReceiptDetail =
    state === ReceiptUploadState.ReceiptDetail && !!receipt;

  if (showReceiptDetail) {
    return (
      <SunshineCard.Group>
        {receipt ? (
          <ReceiptUploadItem onRemove={onReceiptRemoved} receipt={receipt} />
        ) : null}
      </SunshineCard.Group>
    );
  }

  const handleSuccessfulUpload = async (
    uploadedFile: UploadedFileInfo,
  ): Promise<void> => {
    const { bucketName, fileRef, url } = uploadedFile;
    onReceiptUploaded({
      bucketName,
      fileRef,
      url,
    });
    setState(ReceiptUploadState.ReceiptDetail);
  };

  return (
    <Box backgroundColor="grey.200" borderRadius="radius-16">
      <Upload
        backgroundColor="inherit"
        dashedBorderColor="grey.600"
        errorMessage={locales.error}
        fileTypes={FILE_TYPE.ALLOWED_DOCUMENTS}
        onSuccessfulUpload={handleSuccessfulUpload}
        uploadFile={uploadReceipt}
        withLoader
      >
        <Center padding="space-16">
          <UploadMessage
            dragAndDropMessage={locales.dragAndDrop}
            imageElement={<Image boxSize="120px" src={clipboardImg} />}
          />
        </Center>
      </Upload>
    </Box>
  );
};

export default ReceiptUpload;
